/** Громкость и «без звука»: стор + localStorage (в try/catch — в приватном режиме и превью он бросает). */
import { createStore, type Store } from "../app/store";

export interface AudioSettings {
  muted: boolean;
  /** Общая громкость, 0..1. */
  volume: number;
}

export const AUDIO_KEY = "nfg.audio";
export const DEFAULT_SETTINGS: AudioSettings = { muted: false, volume: 1 };

type StorageLike = Pick<Storage, "getItem" | "setItem">;

function defaultStorage(): StorageLike | undefined {
  try {
    return globalThis.localStorage;
  } catch {
    return undefined;
  }
}

function clamp01(v: number): number {
  return Number.isFinite(v) ? Math.min(1, Math.max(0, v)) : 1;
}

export function loadSettings(storage: StorageLike | undefined = defaultStorage()): AudioSettings {
  try {
    const raw = storage?.getItem(AUDIO_KEY);
    if (!raw) return { ...DEFAULT_SETTINGS };
    const p = JSON.parse(raw) as Partial<AudioSettings>;
    return {
      muted: p.muted === true,
      volume: typeof p.volume === "number" ? clamp01(p.volume) : DEFAULT_SETTINGS.volume,
    };
  } catch {
    return { ...DEFAULT_SETTINGS };
  }
}

export function saveSettings(s: AudioSettings, storage: StorageLike | undefined = defaultStorage()): void {
  try {
    storage?.setItem(AUDIO_KEY, JSON.stringify(s));
  } catch {
    /* не сохранилось — работает до перезагрузки */
  }
}

export const audioSettings: Store<AudioSettings> = createStore(loadSettings());

export function setMuted(muted: boolean, store = audioSettings, storage?: StorageLike): void {
  store.set((s) => (s.muted === muted ? s : { ...s, muted }));
  saveSettings(store.get(), storage ?? defaultStorage());
}

export function toggleMuted(store = audioSettings, storage?: StorageLike): boolean {
  setMuted(!store.get().muted, store, storage);
  return store.get().muted;
}

export function setVolume(volume: number, store = audioSettings, storage?: StorageLike): void {
  const v = clamp01(volume);
  store.set((s) => (s.volume === v ? s : { ...s, volume: v }));
  saveSettings(store.get(), storage ?? defaultStorage());
}
